import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
// import { toast } from 'react-hot-toast';
import OrderCard from '@/components/OrderCard';
import ConfirmReceiptDialog from '@/components/ConfirmReceiptDialog';
import OrderDetailsModal from '@/components/OrderDetailsModal';
import { getMyPurchases, confirmPurchaseReceipt, getPurchaseDetails } from '@/api/purchase';
import TabWithFilter from '@/components/TabWithFilter';
import Button from '@/components/Button';
import dondonGreyLogo from '@/assets/dondon_grey.svg';

// order status: PAID, SELLER_SHIPPED, WAREHOUSE_RECEIVED, VERIFYING, WAREHOUSE_SHIPPED, BUYER_RECEIVED, COMPLETED, CANCELLED

const ORDERS_STATE_KEY = 'orders_page_state';

const getOrderKey = (order) => {
  return order.id;
};

const OrdersPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('ALL');
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [confirmReceiptDialogOpen, setConfirmReceiptDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmingReceipt, setConfirmingReceipt] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [accumulatedOrders, setAccumulatedOrders] = useState([]);
  const [cachedNextCursor, setCachedNextCursor] = useState(null);
  const [hasMoreFromCache, setHasMoreFromCache] = useState(false);
  const lastLoadedCursorRef = useRef(null);
  const [restoredFromCache, setRestoredFromCache] = useState(false);
  const restoredEntriesRef = useRef(false);
  const lastRefetchCursorRef = useRef(null);

  // State for specific order detail modal
  const [specificOrderModalOpen, setSpecificOrderModalOpen] = useState(false);
  const [specificOrderId, setSpecificOrderId] = useState(null);

  const { data: ordersData, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['purchases', activeTab, dateRange.startDate, dateRange.endDate, cursor],
    queryFn: () => getMyPurchases({
      status: activeTab,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      cursor
    })
  });

  // Query for specific order details
  const { data: specificOrderData } = useQuery({
    queryKey: ['purchase-details', specificOrderId],
    queryFn: () => getPurchaseDetails(specificOrderId),
    enabled: !!specificOrderId
  });

  // Restore cached state (accumulated data + next cursor) on mount or when filters change
  useEffect(() => {
    const cacheKey = `${ORDERS_STATE_KEY}_${activeTab}_${dateRange.startDate || ''}_${dateRange.endDate || ''}`;
    const cachedState = sessionStorage.getItem(cacheKey);
    if (cachedState) {
      try {
        const parsed = JSON.parse(cachedState);
        if (Array.isArray(parsed.accumulated)) {
          setAccumulatedOrders(parsed.accumulated);
          restoredEntriesRef.current = parsed.accumulated.length > 0;
        }
        if (typeof parsed.nextCursor === 'string' || parsed.nextCursor === null) {
          setCachedNextCursor(parsed.nextCursor);
          // When restored from cache, use nextCursor as lastLoadedCursorRef
          // This is the cursor we need to continue loading from
          lastLoadedCursorRef.current = parsed.nextCursor ?? null;
        }
        if (typeof parsed.hasMore === 'boolean') {
          setHasMoreFromCache(parsed.hasMore);
        }
      } catch (error) {
        console.warn('Failed to parse orders cache', error);
        sessionStorage.removeItem(cacheKey);
      }
    } else {
      // No cache found, reset state
      setAccumulatedOrders([]);
      setCachedNextCursor(null);
      setHasMoreFromCache(false);
      lastLoadedCursorRef.current = null;
      restoredEntriesRef.current = false;
    }
    setRestoredFromCache(true);
  }, [activeTab, dateRange.startDate, dateRange.endDate]);

  // Clear cached state on full page reload/close
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Clear all orders page state keys
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(ORDERS_STATE_KEY)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Update accumulated orders when new data arrives
  useEffect(() => {
    if (!ordersData?.data) {
      return;
    }

    const incoming = ordersData.data;
    const incomingKeys = new Set(incoming.map(getOrderKey));
    const shouldPreserveCachedMeta = restoredEntriesRef.current && cursor === null;

    // Check if this is a refetch with the same cursor (to avoid duplicate data)
    const isRefetchWithSameCursor = lastRefetchCursorRef.current === cursor && cursor !== null;
    
    setAccumulatedOrders((prev) => {
      // cursor === null means we are handling the first page
      if (!cursor) {
        // Check if this is a refetch of the first page (when we have accumulated data from subsequent pages)
        const isRefetchOfFirstPage = lastRefetchCursorRef.current === null && prev.length > 0;
        
        if (!restoredEntriesRef.current && !isRefetchOfFirstPage) {
          // Not restored from cache and not a refetch: fresh entry into OrdersPage
          // Rely only on server data
          return [...incoming];
        }

        // Restored from cache or refetch of first page: preserve subsequent pages
        // Keep the original behavior of "first page on top, cached/subsequent pages below"
        const filteredPrev = prev.filter((item) => !incomingKeys.has(getOrderKey(item)));
        return [...incoming, ...filteredPrev];
      }

      // cursor is not null: this is a subsequent page
      // If this is a refetch with the same cursor, update existing items without appending
      if (isRefetchWithSameCursor) {
        // Update existing items in place, preserving items from other pages
        // Find the range of items that belong to this cursor's page and replace them
        const filteredPrev = prev.filter((item) => !incomingKeys.has(getOrderKey(item)));
        return [...filteredPrev, ...incoming];
      }

      // Normal pagination: de-duplicate then append
      const filteredPrev = prev.filter((item) => !incomingKeys.has(getOrderKey(item)));
      return [...filteredPrev, ...incoming];
    });

    // Update refetch cursor tracking
    // Track the cursor used for refetch to detect refetch operations
    lastRefetchCursorRef.current = cursor;

    if (!shouldPreserveCachedMeta) {
      setCachedNextCursor(ordersData.cursor ?? null);
      setHasMoreFromCache(Boolean(ordersData.cursor));
      lastLoadedCursorRef.current = cursor ?? null;
      restoredEntriesRef.current = false;
    }
  }, [ordersData, cursor]);

  // Persist accumulated data + pagination info to sessionStorage
  useEffect(() => {
    if (!restoredFromCache) return;
    const cacheKey = `${ORDERS_STATE_KEY}_${activeTab}_${dateRange.startDate || ''}_${dateRange.endDate || ''}`;
    const stateToCache = {
      accumulated: accumulatedOrders,
      nextCursor: cachedNextCursor ?? null,
      hasMore: hasMoreFromCache,
      lastCursor: lastLoadedCursorRef.current ?? null
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(stateToCache));
  }, [accumulatedOrders, cachedNextCursor, hasMoreFromCache, restoredFromCache, activeTab, dateRange.startDate, dateRange.endDate]);

  // Handle URL parameters for specific order
  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (orderId) {
      setSpecificOrderId(orderId);
      setSpecificOrderModalOpen(true);
    }
  }, [searchParams]);

  // Handle location state for opening order modal from notification
  useEffect(() => {
    const openOrderId = location.state?.openOrderId;
    if (openOrderId) {
      setSpecificOrderId(openOrderId);
      setSpecificOrderModalOpen(true);
      // Clear state from location to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const tabs = [
    { value: 'ALL', label: t('orders.tabs.all') },
    { value: 'PENDING_SHIPMENT', label: t('orders.tabs.pendingShipment') },
    { value: 'SHIPPED', label: t('orders.tabs.pendingReceipt') },
    { value: 'FINISHED', label: t('orders.tabs.completed') }
  ];

  const handleSearch = (dateRange) => {
    setDateRange(dateRange);
    setCursor(null); // Reset pagination when filter changes
    setCachedNextCursor(null);
    setHasMoreFromCache(false);
    lastLoadedCursorRef.current = null;
    restoredEntriesRef.current = false;
    lastRefetchCursorRef.current = null;
  };

  const handleReset = () => {
    setDateRange({ startDate: null, endDate: null });
    setCursor(null); // Reset pagination when filter changes
    setCachedNextCursor(null);
    setHasMoreFromCache(false);
    lastLoadedCursorRef.current = null;
    restoredEntriesRef.current = false;
    lastRefetchCursorRef.current = null;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setDateRange({ startDate: null, endDate: null }); // Reset date range when tab changes
    setCursor(null); // Reset pagination when tab changes
    setCachedNextCursor(null);
    setHasMoreFromCache(false);
    lastLoadedCursorRef.current = null;
    restoredEntriesRef.current = false;
    lastRefetchCursorRef.current = null;
  };

  const nextCursorToUse = restoredEntriesRef.current
    ? lastLoadedCursorRef.current
    : (ordersData?.cursor ?? cachedNextCursor);

  const handleLoadMore = () => {
    // Calculate the cursor to use at the moment of click
    // This ensures we get the latest value, especially important for back navigation
    const cursorToUse = restoredEntriesRef.current
      ? lastLoadedCursorRef.current
      : (ordersData?.cursor ?? cachedNextCursor);
    
    if (cursorToUse) {
      setCursor(cursorToUse);
      // Clear cached values after using them
      setCachedNextCursor(null);
      setHasMoreFromCache(false);
      // Don't reset restoredEntriesRef here - let it be reset in the data update effect
      // when shouldPreserveCachedMeta becomes false (i.e., when cursor is not null)
    }
  };

  const handleOpenConfirmReceipt = (order) => {
    setSelectedOrder(order);
    setConfirmReceiptDialogOpen(true);
  };

  const handleCloseConfirmReceipt = () => {
    setConfirmReceiptDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleConfirmReceipt = async (orderId) => {
    try {
      setConfirmingReceipt(true);
      await confirmPurchaseReceipt(orderId);
      // toast.success(t('orders.confirmReceipt.success'));
      // Don't reset cursor - keep current cursor state to preserve pagination
      // The refetch will use the current cursor, and the data update logic will handle
      // updating existing items without losing accumulated data
      refetch();
      setConfirmReceiptDialogOpen(false);
    } catch (error) {
      console.error('Error confirming receipt:', error);
      // toast.error(t('orders.confirmReceipt.error'));
    } finally {
      setConfirmingReceipt(false);
    }
  };

  const handleOpenSpecificOrderModal = (orderId) => {
    setSpecificOrderId(orderId);
    setSpecificOrderModalOpen(true);
    // Update URL to include orderId parameter
    setSearchParams({ orderId });
  };

  const handleCloseSpecificOrderModal = () => {
    setSpecificOrderModalOpen(false);
    setSpecificOrderId(null);
    // Remove orderId parameter from URL
    setSearchParams({});
  };

  const hasMoreOrders = restoredEntriesRef.current
    ? hasMoreFromCache
    : Boolean(nextCursorToUse);
  // const showFilters = !isLoading && (accumulatedOrders.length > 0 || cursor || activeTab !== 'ALL' || dateRange.startDate || dateRange.endDate);

  // Determine if any filter is active
  const isAnyFilterActive = activeTab !== 'ALL' || dateRange.startDate || dateRange.endDate;

  // Determine if the filter UI (tabs and headers) should be shown
  const shouldShowFilterUI = isAnyFilterActive || accumulatedOrders.length > 0;

  return (
    <div>
      <div className="mx-auto bg-white rounded-lg shadow-sm p-6">
        {/* Header */}
        <h1 className="text-lg font-bold mb-6">{t('me.menu.orders')}</h1>

        {shouldShowFilterUI && (
          <TabWithFilter
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onSearch={handleSearch}
            onReset={handleReset}
          />
        )}

        {/* Table Headers */}
        {shouldShowFilterUI && (
          <div className="mt-4 grid grid-cols-[1fr,108px,108px,108px,180px] gap-6 py-1.5 bg-[#f8f8f8] text-sm text-center text-gray-500">
            <div>{t('orders.columns.orderInfo')}</div>
            <div>{t('orders.columns.grade')}</div>
            <div>{t('orders.columns.price')}</div>
            <div>{t('orders.columns.status')}</div>
            <div>{t('orders.columns.action')}</div>
          </div>
        )}

        {/* Orders List */}
        {isLoading && !cursor && !shouldShowFilterUI ? (
          <div className="h-[800px] flex justify-center items-center">
            <span className="text-gray-400">{t('common.loading')}</span>
          </div>
        ) : isLoading && !cursor && shouldShowFilterUI ? (
          <div className="w-full flex justify-center items-center py-10">
            <p>{t('common.loading')}</p>
          </div>
        ) : isError ? (
          <div className="w-full flex justify-center items-center py-10">
            <p>{t('common.error')}</p>
          </div>
        ) : accumulatedOrders.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {accumulatedOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onConfirmReceipt={handleOpenConfirmReceipt}
                onStatusClick={handleOpenSpecificOrderModal}
              />
            ))}
          </div>
        ) : !shouldShowFilterUI ? ( // This covers the initial empty state when no filters are active
          <div className="h-[800px] flex flex-col justify-center items-center gap-6">
            <img src={dondonGreyLogo} alt="DonDon" className="w-24 h-16 opacity-50" />
            <div className="text-center text-[#aaaaaa] text-sm font-normal leading-tight">
              {t('me.orders.empty_state_main')}
            </div>
          </div>
        ) : ( // This covers the case where filters are active but no orders match
          <div className="w-full flex justify-center items-center py-10">
            <p>{t('me.orders.noOrders')}</p>
          </div>
        )}

        {/* Loading indicator for pagination */}
        {isFetching && cursor && (
          <div className="w-full flex justify-center items-center py-4">
            <p>{t('common.loading')}</p>
          </div>
        )}

        {/* Load More Button */}
        {hasMoreOrders && (
          <div className="flex justify-center mt-8">
            <Button 
              variant="default" 
              size="m" 
              onClick={handleLoadMore}
              disabled={isFetching}
            >
              {isFetching && cursor ? t('common.loading') : t('home.actions.load_more')}
            </Button>
          </div>
        )}

        {/* Confirm Receipt Dialog */}
        <ConfirmReceiptDialog
          isOpen={confirmReceiptDialogOpen}
          onClose={handleCloseConfirmReceipt}
          onConfirm={handleConfirmReceipt}
          order={selectedOrder}
          isLoading={confirmingReceipt}
        />
      </div>

      <div className='w-full mt-6 pb-20 flex-col justify-start items-start'>
        <div className="justify-center text-[#111111] text-sm font-bold">
          {t('orders.policies.title')}
        </div>

        <div className="relative text-[#111111] text-sm font-bold mt-2">
          {t('orders.policies.cancellation.title')}
        </div>

        <div className="justify-center text-[#aaaaaa] text-xs font-normal mt-2">
          {t('orders.policies.cancellation.credit_card_line_pay')}
        </div>

        <div className="justify-center text-[#aaaaaa] text-xs font-normal mt-1">
          {t('orders.policies.cancellation.convenience_store')}
        </div>

        <div className="relative text-[#111111] text-sm font-bold mt-2">
          {t('orders.policies.exchange_return.title')}
        </div>

        <div className="justify-center text-[#aaaaaa] text-xs font-normal mt-2">
          {t('orders.policies.exchange_return.customer_reason')}
        </div>

        <div className="justify-center text-[#aaaaaa] text-xs font-normal mt-1">
          {t('orders.policies.exchange_return.defective_wrong')}
        </div>
      </div>

      {/* Specific Order Details Modal */}
      {specificOrderModalOpen && specificOrderData?.data && (
        <OrderDetailsModal
          isOpen={specificOrderModalOpen}
          onClose={handleCloseSpecificOrderModal}
          order={specificOrderData.data}
          onConfirmReceipt={handleOpenConfirmReceipt}
        />
      )}
    </div>
  );
};

export default OrdersPage; 